/** @jsxImportSource @emotion/react */
import { SwipeableDrawer } from '@mui/material';
import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import DrawerIcon, { DrawerIconProps } from './drawerIcon';
import { drawerWrapper } from './style';
import { SerializedStyles } from '@emotion/react';

export const DrawerContext = createContext<{
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}>({ drawerOpen: false, setDrawerOpen: () => false });

export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (context === undefined) {
    throw new Error(`useDrawer must be used within a DrawerContext.Provider`);
  }
  return context;
};

export const DrawerProvider = ({ children }: { children: ReactNode }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <DrawerContext.Provider value={{ drawerOpen, setDrawerOpen }}>
      {children}
    </DrawerContext.Provider>
  );
};

function Drawer({
  drawerWrapperCss,
  closingDrawerIconProps,
  anchor,
  children,
  cypressId,
  ...props
}: DrawerProps) {
  const { drawerOpen, setDrawerOpen } = useDrawer();

  return (
    <>
      <DrawerIcon
        cypressId={`${cypressId}-open`}
        onClick={() => setDrawerOpen(true)}
        {...props}
      />
      <SwipeableDrawer
        anchor={anchor}
        open={!!drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        BackdropProps={{ invisible: true }}
      >
        <div css={{ ...drawerWrapper, ...(drawerWrapperCss || {}) }}>
          <DrawerIcon
            cypressId={`${cypressId}-close`}
            expanded={drawerOpen}
            onClick={() => setDrawerOpen(false)}
            {...closingDrawerIconProps}
          />
          <br />
          {children}
        </div>
      </SwipeableDrawer>
    </>
  );
}

type DrawerProps = PropsWithChildren & {
  drawerWrapperCss: SerializedStyles | SerializedStyles[];
  closingDrawerIconProps: Partial<DrawerIconProps>;
  anchor?: 'top' | 'left' | 'bottom' | 'right';
  cypressId?: string;
};

export default Drawer;
